import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, clean, sqlEscape } from "../utils/sqlClient";
import { fmtMoney, fmtDate } from "../utils/format";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Plain inline SQL params (category/trade filters) get the strip-based clean()
// used everywhere else in this codebase; the JSON payload sent to
// sp_SaveReferencePricing needs proper '' escaping instead — same distinction
// made in Estimate.jsx's line-item save, kept consistent here.
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  Category: "",
  Trade: "",
  ItemDescription: "",
  Unit: "",
  LowPrice: "",
  MidPrice: "",
  HighPrice: "",
  Region: "",
  EffectiveDate: todayISO(),
  Source: "Manual",
  Notes: "",
};

function SourceTag({ source }) {
  const isComputed = source === "Computed";
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
      background: isComputed ? "#ede9fe" : "#f3f4f6",
      color: isComputed ? "#5b21b6" : "var(--text3)",
    }}>
      {source || "—"}
    </span>
  );
}

export default function ReferencePricingLibrary() {
  const { accounts } = useMsal();
  const user = accounts[0];
  const isAdmin = useIsAdmin();

  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(null);
  const [search, setSearch]       = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDays, setRefreshDays] = useState(90);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [historyFor, setHistoryFor] = useState(null); // ReferenceID of expanded row
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = () => {
    setLoading(true);
    sqlRead("EXEC sp_GetReferencePricing")
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const categories = useMemo(
    () => ["All", ...new Set(items.map(i => i.Category))].filter(Boolean),
    [items]
  );

  const filtered = items.filter(i => {
    if (filterCategory !== "All" && i.Category !== filterCategory) return false;
    if (search && !i.ItemDescription?.toLowerCase().includes(search.toLowerCase()) && !i.Trade?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = {};
  filtered.forEach(i => {
    const c = i.Category || "(No Category)";
    if (!grouped[c]) grouped[c] = [];
    grouped[c].push(i);
  });

  const handleSave = async () => {
    if (!form?.Category?.trim() || !form?.Trade?.trim() || !form?.ItemDescription?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        Category: form.Category.trim(),
        Trade: form.Trade.trim(),
        ItemDescription: form.ItemDescription.trim(),
        Unit: form.Unit || null,
        LowPrice: form.LowPrice === "" ? null : Number(form.LowPrice),
        MidPrice: form.MidPrice === "" ? null : Number(form.MidPrice),
        HighPrice: form.HighPrice === "" ? null : Number(form.HighPrice),
        Region: form.Region || null,
        EffectiveDate: form.EffectiveDate || todayISO(),
        Source: form.Source || "Manual",
        Notes: form.Notes || null,
        UpdatedBy: user?.name || user?.username || null,
      };
      await sqlRead(`EXEC sp_SaveReferencePricing @payload='${sqlEscape(JSON.stringify(payload))}'`);
      setForm(null);
      load();
    } catch (e) {
      console.error(e);
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const rows = await sqlRead(`EXEC sp_RefreshReferencePricing @lookback_days=${Number(refreshDays) || 90}`);
      setRefreshMsg(`Added ${rows[0]?.RowsInserted ?? 0} computed price point(s) from recent quotes.`);
      load();
    } catch (e) {
      setRefreshMsg(`Refresh failed: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleHistory = async (row) => {
    if (historyFor === row.ReferenceID) { setHistoryFor(null); return; }
    setHistoryFor(row.ReferenceID);
    setHistoryLoading(true);
    try {
      const rows = await sqlRead(
        `EXEC sp_GetReferencePricing @category='${clean(row.Category)}', @trade='${clean(row.Trade)}', @include_history=1`
      );
      setHistoryRows(rows.filter(r => r.ItemDescription === row.ItemDescription));
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="panel-title">Reference Pricing Library</div>
          <div className="panel-sub">
            The master price book estimators draw from when pricing a line item. Every entry is dated history —
            "Update price" always adds a new point rather than overwriting, so trend over time is never lost.
          </div>
        </div>
        <button
          onClick={() => setForm({ ...EMPTY_FORM })}
          style={{ height: 32, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--cs-navy)", border: "none", borderRadius: "var(--radius)", cursor: "pointer", flexShrink: 0 }}
        >
          + New price point
        </button>
      </div>

      {/* Refresh from quotes */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, padding: "10px 14px", background: "#f8fafc", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)" }}>
        <span style={{ fontSize: 12, color: "var(--text2)" }}>Recompute Low/Mid/High from real vendor quotes in the trailing</span>
        <input type="number" value={refreshDays} onChange={e => setRefreshDays(e.target.value)} style={{ width: 60, height: 26, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 6px", fontSize: 12, textAlign: "center" }} />
        <span style={{ fontSize: 12, color: "var(--text2)" }}>days</span>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: "var(--radius)", background: "#fff", color: "var(--cs-blue)", border: "1px solid var(--cs-blue)", cursor: "pointer" }}
        >
          {refreshing ? "Refreshing…" : "Refresh from quotes"}
        </button>
        {refreshMsg && <span style={{ fontSize: 12, color: "var(--text3)" }}>{refreshMsg}</span>}
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search item or trade…"
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", outline: "none", minWidth: 200 }}
        />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ height: 28, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, background: "#fff", outline: "none" }}>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: "auto" }}>
          {filtered.length} item{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Editor */}
      {form && (
        <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: 16, marginBottom: 18, background: "#FAFBFC" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>New price point</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 10 }}>
            <Field label="Category *">
              <input value={form.Category} onChange={e => setForm(f => ({ ...f, Category: e.target.value }))} list="category-list" style={inputStyle} />
              <datalist id="category-list">
                {categories.filter(c => c !== "All").map(c => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="Trade *">
              <input value={form.Trade} onChange={e => setForm(f => ({ ...f, Trade: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="Item description *">
              <input value={form.ItemDescription} onChange={e => setForm(f => ({ ...f, ItemDescription: e.target.value }))} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Field label="Unit">
              <input value={form.Unit} onChange={e => setForm(f => ({ ...f, Unit: e.target.value }))} placeholder="EA" style={inputStyle} />
            </Field>
            <Field label="Low">
              <input type="number" value={form.LowPrice} onChange={e => setForm(f => ({ ...f, LowPrice: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="Mid">
              <input type="number" value={form.MidPrice} onChange={e => setForm(f => ({ ...f, MidPrice: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="High">
              <input type="number" value={form.HighPrice} onChange={e => setForm(f => ({ ...f, HighPrice: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="Effective date">
              <input type="date" value={form.EffectiveDate} onChange={e => setForm(f => ({ ...f, EffectiveDate: e.target.value }))} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 10 }}>
            <Field label="Region">
              <input value={form.Region} onChange={e => setForm(f => ({ ...f, Region: e.target.value }))} style={inputStyle} />
            </Field>
            <Field label="Source">
              <input value={form.Source} onChange={e => setForm(f => ({ ...f, Source: e.target.value }))} placeholder="Manual, Vendor Catalog…" style={inputStyle} />
            </Field>
            <Field label="Notes">
              <input value={form.Notes} onChange={e => setForm(f => ({ ...f, Notes: e.target.value }))} style={inputStyle} />
            </Field>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button
              onClick={handleSave}
              disabled={saving || !form.Category?.trim() || !form.Trade?.trim() || !form.ItemDescription?.trim()}
              style={{ height: 30, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--cs-navy)", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
            >
              {saving ? "Saving…" : "Add price point"}
            </button>
            <button onClick={() => setForm(null)} style={{ height: 30, padding: "0 12px", fontSize: 12, color: "var(--text2)", background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Item list */}
      {loading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading library…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          No price points match your filters.
        </div>
      ) : (
        Object.entries(grouped).map(([category, cItems]) => (
          <div key={category} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {category}
              </div>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
              <div style={{ fontSize: 11, color: "var(--text3)" }}>{cItems.length}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {cItems.map(item => (
                <div key={item.ReferenceID}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10,
                    border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                    padding: "8px 12px", background: "#fff",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: "var(--text)" }}>{item.ItemDescription}</span>
                      <span style={{ fontSize: 10, marginLeft: 6, color: "var(--text3)", padding: "1px 6px", borderRadius: 100, background: "#f3f4f6" }}>
                        {item.Trade}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--text3)", flexShrink: 0 }}>
                      <span>Low {fmtMoney(item.LowPrice)}</span>
                      <span style={{ fontWeight: 700, color: "var(--text)" }}>Mid {fmtMoney(item.MidPrice)}</span>
                      <span>High {fmtMoney(item.HighPrice)}</span>
                    </div>

                    <span style={{ fontSize: 11, color: "var(--text3)", flexShrink: 0 }}>{fmtDate(item.EffectiveDate)}</span>
                    <SourceTag source={item.Source} />

                    <button onClick={() => toggleHistory(item)}
                      style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                      {historyFor === item.ReferenceID ? "Hide history" : "History"}
                    </button>
                    <button onClick={() => setForm({ ...EMPTY_FORM, ...item, EffectiveDate: todayISO(), Source: "Manual" })}
                      style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                      Update price
                    </button>
                  </div>

                  {historyFor === item.ReferenceID && (
                    <div style={{ marginLeft: 12, marginTop: 4, padding: "8px 12px", background: "#fafbfc", border: "1px dashed var(--cs-border)", borderRadius: "var(--radius)" }}>
                      {historyLoading ? (
                        <div style={{ fontSize: 11, color: "var(--text3)" }}>Loading history…</div>
                      ) : historyRows.length <= 1 ? (
                        <div style={{ fontSize: 11, color: "var(--text3)" }}>No earlier price points on file.</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {historyRows.map(h => (
                            <div key={h.ReferenceID} style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--text3)" }}>
                              <span style={{ width: 90 }}>{fmtDate(h.EffectiveDate)}</span>
                              <span style={{ width: 200 }}>Low {fmtMoney(h.LowPrice)} · Mid {fmtMoney(h.MidPrice)} · High {fmtMoney(h.HighPrice)}</span>
                              <SourceTag source={h.Source} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 30,
  border: "1px solid var(--cs-border)",
  borderRadius: "var(--radius)",
  padding: "0 9px",
  fontSize: 12,
  color: "var(--text)",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};
